import { createContext, useContext } from 'react';

export interface CurrentUser {
  userId: string;
  gitLabUserId: number;
  username: string;
  name: string;
  email: string | null;
  avatarUrl: string | null;
  productRole: string;
  permittedProjectCount: number;
}

export interface AuthState {
  status: 'restoring' | 'signedIn' | 'signedOut';
  user: CurrentUser | null;
  signOut: () => Promise<void>;
  completeSignIn: (handoffTicket: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

/**
 * The session context and the hook that reads it.
 *
 * @remarks
 * These live apart from the provider component for a mechanical reason rather than a
 * design one: a module that exports both components and plain values breaks React Fast
 * Refresh, so an edit to the provider reloads the whole application and drops the state
 * being debugged. Keeping the non-component exports here means the provider file exports
 * exactly one component.
 *
 * Nothing the context carries is an authorization decision. The role is here to choose what
 * the interface offers; the server re-decides every request, because a client that trusts a
 * role claim is a client that can be lied to by editing memory
 * (`frontend-standards.md` section 5).
 */
export const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider.');
  }

  return context;
}
